import type { Vector2 } from 'three'
import { Euler, Quaternion, Vector3 } from 'three'
import type { Vec2Data } from '@/entities/model3d'

/**
 * Given a set of 2D UV coordinates, return the conversion to 3D world space
 */
export const uvToWorld = (uv: Vec2Data, alloc = new Vector3()): Vector3 => {
  let sc = Math.sin(uv.y * Math.PI)

  return alloc.set(-Math.cos(uv.x * Math.PI * 2) * sc, -Math.cos(uv.y * Math.PI), -Math.sin(uv.x * Math.PI * 2) * sc)
}

// Vector is a point on the unit sphere. MUST be normalized
// https://stackoverflow.com/questions/48494389/how-does-this-code-sample-from-a-spherical-map
export const worldToUv = ({ x, y, z }: Vector3): Vec2Data => {
  const u = Math.atan2(z, x) / (2 * Math.PI) + 0.5
  const v = Math.asin(y) / Math.PI + 0.5

  return { x: u, y: v }
}

// Get polar opposite coordinate of a UV coordinate
export const uvOpposite = ({ x, y }: Vec2Data): Vec2Data => {
  return {
    x: (x + 0.5) % 1,
    y: 1 - y,
  }
}

/**
 * Crudely approximate the length of the curve when traversing a sphere where
 * the curve is represented as a straight line on a UV mapping of said sphere.
 *
 * The curve is not necessarily a straight line on the sphere. Since UV doesn't
 * map linearly, a straight, diagonal line on the 2D UV map can be curved or
 * even S-shaped (if it crosses the equator) on the 3D sphere.
 *
 * @param from the UV coordinate to start at
 * @param diff the vector to move by to reach the UV coordinate of the finish
 */
const STEPS = 10
export const uvArcDistance = (from: Vector2, diff: Vector2): number => {
  return Array.from(Array(STEPS)) // array of STEPS elements
    .map((_, i) => i / STEPS) // fill array with 0 ≤ x < 1 for each step
    .map(x => {
      const stepFrom = from.clone().addScaledVector(diff, x)
      const stepTo = from.clone().addScaledVector(diff, x + 1 / STEPS)
      // straight line from this step to next step (last step will == from + diff)
      return uvToWorld(stepTo).distanceTo(uvToWorld(stepFrom))
    })
    .reduce((a, b) => a + b) // add all the segments together
}

// camera pointing down the z-axis
const INITIAL_VECTOR = new Vector3(0, 0, -1)

/**
 * Given a set of camera angles in radians, return the vector
 */
export const getLookAtPoint = ({ x, y }: Vec2Data) => {
  const euler = new Euler(x, y - Math.PI / 2, 0, 'YXZ')
  return INITIAL_VECTOR.clone().applyEuler(euler)
}

/**
 * Performs the inverse of getLookAtPoint to return spherical coordinates in radians (where r=1)
 * @param lookAtPoint the world coordinate on the unit sphere
 */
export const getSphericalCoord = (lookAtPoint: Vector3): Vec2Data => {
  const q = new Quaternion().setFromUnitVectors(INITIAL_VECTOR, lookAtPoint)
  const euler = new Euler().setFromQuaternion(q, 'YXZ')
  return { x: euler.x, y: euler.y + Math.PI / 2 }
}

const vfovToHfov = (fov: number, width: number, height: number): number => 2 * Math.atan((width * Math.tan(fov / 2)) / height)
const hfovToVfov = (fov: number, width: number, height: number): number => 2 * Math.atan((height * Math.tan(fov / 2)) / width)

// Gets limits of a vertical field of view given a general fov maximum (horizontal and vertical)
// as well as the image width. Assumed to be related to 360 viewer
// maxFov expected in radians
export const getVfovBounds = (maxVfov: number, width: number, height: number): { min: number; max: number } => {
  // Max is either the max fov or the vfov after the hfov is limited to max
  const hfov = vfovToHfov(maxVfov, width, height)
  if (hfov > maxVfov) {
    maxVfov = hfovToVfov(maxVfov, width, height)
  }

  // Limits the fov to a certain resolution (2048). Pulled from Marzipano
  // The value 2048 should probably be based on our texture dimensions but
  // we had it hard coded for Marzipano so it's hardcoded here
  // Marzipano reference: https://github.com/google/marzipano/blob/7936a4e0791fcfe55d199c03080be4f89ccf71bd/src/views/Rectilinear.js#L955
  const minVfov = Math.min(maxVfov - 0.001, 2 * Math.atan(height / 2048))

  return { min: minVfov, max: maxVfov }
}

export const getFovRatio = (fovRadians1: number, fovRadians2: number) => {
  return Math.tan(fovRadians1 / 2) / Math.tan(fovRadians2 / 2)
}

// same formula as above, with different unknown variable
export const getChangedFov = (startFovRadians: number, distanceDelta: number) => {
  return 2 * Math.atan(distanceDelta * Math.tan(startFovRadians / 2))
}

export function uvDistance(uv1: Vec2Data, uv2: Vec2Data): number {
  let hdist = uv1.x - uv2.x
  if (Math.abs(hdist) > Math.abs(hdist + 1)) {
    hdist += 1
  }
  if (Math.abs(hdist) > Math.abs(hdist - 1)) {
    hdist -= 1
  }
  hdist *= 2
  let ydist = uv1.y - uv2.y
  return Math.sqrt(hdist * hdist + ydist * ydist)
}

/**
 * Returns a decimal representation of a number. For decimal fields on backend.
 */
export function toDecimal(n: number | string | undefined, digits: number): string | undefined {
  if (n === undefined) return n
  // +2 for leading 0.
  return `${n}`.substring(0, digits + 2)
}

export function uvToCameraCoords(uv: { x: number; y: number }): { pan_x: number; pan_y: number } {
  const worldPoint = uvToWorld({ x: uv.x, y: 1 - uv.y })
  const angle = getSphericalCoord(worldPoint)
  return { pan_x: angle.x, pan_y: angle.y }
}
